export interface VacationBlockBracket {
  key: 'upTo9' | 'from10to15' | 'from16to30' | 'moreThan30';
  label: string;
  blockBeforeDays: number | null;
  blockBeforeLabel: string;
  blockAfterLabel: string;
  /** Multiplicador aplicado a la duración para saber cuánto sigue bloqueada la agenda después de volver (null = no aplica). */
  blockAfterMultiplier: number | null;
}

export const VACATION_BLOCK_TABLE_HEADERS = {
  days: 'Días off',
  before: 'Bloqueo antes de tus días off',
  after: 'Bloqueo después de volver',
} as const;

const NO_BLOCK_AFTER_TEXT = 'No aplica — tu agenda vuelve a recibir pacientes nuevos en cuanto regresás.';

export const VACATION_BLOCK_BRACKETS: VacationBlockBracket[] = [
  {
    key: 'upTo9',
    label: 'Hasta 9 días',
    blockBeforeDays: null,
    blockBeforeLabel: 'No hay bloqueo previo',
    blockAfterLabel: NO_BLOCK_AFTER_TEXT,
    blockAfterMultiplier: null,
  },
  {
    key: 'from10to15',
    label: '10 a 15 días',
    blockBeforeDays: 30,
    blockBeforeLabel: 'Se bloquea 30 días antes',
    blockAfterLabel: NO_BLOCK_AFTER_TEXT,
    blockAfterMultiplier: null,
  },
  {
    key: 'from16to30',
    label: '16 a 30 días',
    blockBeforeDays: 60,
    blockBeforeLabel: 'Se bloquea 60 días antes',
    blockAfterLabel: NO_BLOCK_AFTER_TEXT,
    blockAfterMultiplier: null,
  },
  {
    key: 'moreThan30',
    label: 'Más de 30 días',
    blockBeforeDays: 90,
    blockBeforeLabel: 'Se bloquea 90 días antes',
    blockAfterLabel: 'Sigue bloqueada al volver, por el doble de los días que estuviste off.',
    blockAfterMultiplier: 2,
  },
];

/** Ejemplos ilustrativos en lenguaje relativo (sin fechas de calendario fijas) para el modal informativo genérico. */
export const VACATION_BLOCK_ILLUSTRATIVE_EXAMPLES: Record<VacationBlockBracket['key'], string> = {
  upTo9: 'Hasta 10 días: si tomás 9 días off, no hay bloqueo antes y volvés a recibir pacientes en cuanto regresás.',
  from10to15: '10 a 15 días: si tomás 13 días off, tu agenda se bloquea 30 días antes de que empieces, y volvés a recibir pacientes en cuanto regresás.',
  from16to30: '16 a 30 días: si tomás 20 días off, tu agenda se bloquea 60 días antes de que empieces, y volvés a recibir pacientes en cuanto regresás.',
  moreThan30: 'Más de 30 días: si tomás 45 días off, tu agenda se bloquea 90 días antes de que empieces, y sigue bloqueada 90 días más después de tu regreso (el doble de tu tiempo off) antes de volver a recibir pacientes nuevos.',
};

export function getVacationBracketIndex(days: number | null | undefined): number | null {
  if (days === null || days === undefined) return null;
  if (days < 10) return 0;
  if (days <= 15) return 1;
  if (days <= 30) return 2;
  return 3;
}

export function getVacationBracket(days: number | null | undefined): VacationBlockBracket | null {
  const idx = getVacationBracketIndex(days);
  return idx === null ? null : VACATION_BLOCK_BRACKETS[idx];
}

export interface VacationBlockDates {
  days: number;
  bracket: VacationBlockBracket;
  /** Fecha desde la que se bloquea la agenda antes de las vacaciones (null si el tramo no tiene bloqueo previo). */
  blockStartDate: Date | null;
  /** Primer día de vuelta al trabajo (día siguiente a "hasta"). */
  returnDate: Date;
  /** Fecha hasta la que sigue bloqueada la agenda después de volver (null si se levanta apenas regresa). */
  blockAfterEndDate: Date | null;
  /** Fecha real desde la que arranca el bloqueo de agenda (from - buffer del tramo). Nunca null, a diferencia de blockStartDate. */
  rangeStart: Date;
  /** Fecha real hasta la que dura el bloqueo de agenda: fin del período, o fin + el doble de los días off si el tramo prolonga el bloqueo al volver. */
  rangeEnd: Date;
}

function addDays(date: Date, amount: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
}

/** Calcula las fechas reales de bloqueo a partir de un rango Desde/Hasta. No cambia la regla de negocio, solo deriva fechas concretas. */
export function computeVacationBlockDates(from: Date, to: Date): VacationBlockDates | null {
  const days = Math.round((to.getTime() - from.getTime()) / 86400000);
  const bracket = getVacationBracket(days);
  if (!bracket) return null;
  const returnDate = addDays(to, 1);
  const blockStartDate = bracket.blockBeforeDays !== null ? addDays(from, -bracket.blockBeforeDays) : null;
  const blockAfterEndDate = bracket.blockAfterMultiplier !== null ? addDays(returnDate, bracket.blockAfterMultiplier * days) : null;
  const rangeStart = blockStartDate ?? from;
  const rangeEnd = bracket.blockAfterMultiplier !== null ? addDays(to, bracket.blockAfterMultiplier * days) : to;
  return { days, bracket, blockStartDate, returnDate, blockAfterEndDate, rangeStart, rangeEnd };
}

export function formatVacationDate(d: Date): string {
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Formato corto dd/mm/aaaa — usado en el detalle de rango de bloqueo dentro de "Modificar período". */
export function formatVacationDateShort(d: Date): string {
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Oración con el rango completo de bloqueo en base a fechas reales — usada en "Modificar período" y en el mail de confirmación. */
export function formatVacationBlockSentence(from: Date, to: Date, formatDate: (d: Date) => string = formatVacationDate): string | null {
  const result = computeVacationBlockDates(from, to);
  if (!result) return null;
  const { bracket, rangeStart, rangeEnd } = result;
  const base = `Tu agenda va a estar bloqueada para pacientes nuevos del ${formatDate(rangeStart)} al ${formatDate(rangeEnd)}.`;
  if (bracket.blockAfterMultiplier !== null) {
    return `${base} Aunque ya hayas vuelto, seguirá bloqueada hasta el ${formatDate(rangeEnd)} por tratarse de una ausencia prolongada.`;
  }
  return `${base} A partir del ${formatDate(addDays(rangeEnd, 1))} volvés a recibir pacientes con normalidad.`;
}

/**
 * Frase de impacto en lenguaje llano para el paso de confirmación previo a guardar
 * ("Continuar" en el modal "Modificar período"). Reutiliza el mismo cálculo de
 * fechas reales que `formatVacationBlockSentence`, con un wording más directo.
 */
export function formatVacationImpactSentence(from: Date, to: Date, formatDate: (d: Date) => string = formatVacationDate): string | null {
  const result = computeVacationBlockDates(from, to);
  if (!result) return null;
  const { blockStartDate, returnDate, blockAfterEndDate } = result;
  if (!blockStartDate) {
    return `No vas a tener bloqueo previo. A partir del ${formatDate(returnDate)}, vas a poder recibir pacientes nuevos otra vez.`;
  }
  if (blockAfterEndDate) {
    return `Tu agenda se bloqueará para pacientes nuevos desde el ${formatDate(blockStartDate)}. Vas a poder recibir pacientes nuevos otra vez a partir del ${formatDate(blockAfterEndDate)}.`;
  }
  return `Tu agenda se bloqueará para pacientes nuevos desde el ${formatDate(blockStartDate)}. A partir del ${formatDate(returnDate)}, vas a poder recibir pacientes nuevos otra vez.`;
}
